import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable } from 'rxjs'

import type {
  MakerFile,
  MakerFilePage,
} from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'
import { requestScopeContext } from '@flowx/designer/shared/api/request-scope.token'

const BASE_PATH = 'appmanager/api/maker-files'

/** The most rows app-manager answers a list with. */
const MAKER_FILES_PAGE_SIZE = 50

/**
 * The maker's own chat files on app-manager: the document center lists, opens and
 * deletes them one conversation at a time. A file lives in the workspace it was uploaded in, which
 * is the conversation's and not always the page's, so each call names it; without one the
 * interceptors stamp the page's. Another maker's file answers 404, as if it did not exist.
 */
@Injectable({ providedIn: 'root' })
export class MakerFilesApi extends ApiHttpService {
  constructor() {
    super(inject<string>('BASE_URL' as any), inject(HttpClient))
  }

  /** One conversation's files, newest first, the failed ones with their reason. */
  list(conversationId: string, workspaceId?: string, page = 0): Observable<MakerFilePage> {
    return this.post(
      `${BASE_PATH}/list`,
      { conversationId, page, size: MAKER_FILES_PAGE_SIZE },
      scoped(workspaceId)
    )
  }

  /** The original bytes: inline for PDFs and images, an attachment for anything else. */
  content(storeEntryUuid: string, workspaceId?: string): Observable<Blob> {
    return this.get(`${BASE_PATH}/${storeEntryUuid}/content`, {
      ...scoped(workspaceId),
      responseType: 'blob',
    })
  }

  /** Index a file that failed processing again. It comes back under a new store entry id. */
  retry(storeEntryUuid: string, workspaceId?: string): Observable<MakerFile> {
    return this.post(`${BASE_PATH}/${storeEntryUuid}/retry`, {}, scoped(workspaceId))
  }

  /** One file and its search index; past messages keep naming it. */
  remove(storeEntryUuid: string, workspaceId?: string): Observable<void> {
    return this.delete(`${BASE_PATH}/${storeEntryUuid}`, scoped(workspaceId))
  }
}

function scoped(workspaceId: string | undefined): {
  context?: ReturnType<typeof requestScopeContext>
} {
  return workspaceId ? { context: requestScopeContext({ type: 'workspace', workspaceId }) } : {}
}
